import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import SaleThread from "../components/SaleThread";
import VinBadge from "../components/VinBadge";
import FinanceHint from "../components/FinanceHint";
import ShareButtons from "../components/ShareButtons";
import HeartButton from "../components/HeartButton";
import MakeOffer from "../components/MakeOffer";
import { Gauge, MapPin, FileCheck, Wrench, Lock, MessageSquare, ShieldCheck, Eye, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SaleDetail() {
  const { listingId } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [l, setL] = useState(null);
  const [hero, setHero] = useState(0);
  const [thread, setThread] = useState(null);
  const [threads, setThreads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [finalPrice, setFinalPrice] = useState("");

  const completeSale = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sales/${listingId}/complete`, { final_price: finalPrice ? Number(finalPrice) : null });
      setL(data); toast.success(`Sold — deposit applied, $${Number(data.balance_due).toLocaleString()} due at handover`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete sale"); } finally { setBusy(false); }
  };

  const load = useCallback(() => api.get(`/sales/${listingId}`).then(({ data }) => setL(data)).catch(() => nav("/buy")), [listingId, nav]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get("reserved") === "1") toast.success("Deposit received — the car is reserved for you");
    if (params.get("cancelled") === "1") toast.message("Reservation cancelled");
  }, [params]);

  const isSeller = user && l && user.id === l.seller_id;
  useEffect(() => {
    if (!user || !l) return;
    if (isSeller || user.is_admin) api.get(`/sales/${listingId}/threads`).then(({ data }) => setThreads(data)).catch(() => {});
  }, [user, l, isSeller, listingId]);

  const openThread = async () => {
    if (!user) { nav("/login"); return; }
    try { const { data } = await api.post(`/sales/${listingId}/threads`); setThread(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not open chat"); }
  };

  const reserve = async () => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/sales/${listingId}/reserve`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start reservation"); setBusy(false); }
  };

  const setStatus = async (status) => {
    try { const { data } = await api.patch(`/sales/${listingId}`, { status }); setL(data); toast.success(`Marked ${status}`); }
    catch { toast.error("Update failed"); }
  };

  if (!l) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  const reserved = l.status === "reserved";
  const mine = reserved && user && l.reserved_by === user.id;
  const expiresIn = reserved && l.reservation_expires_at ? Math.max(0, Math.round((new Date(l.reservation_expires_at) - Date.now()) / 36e5)) : null;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="sale-detail-page">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <div className="relative aspect-[16/10] rounded-3xl overflow-hidden bg-slate-900 rise">
            {l.photos?.[hero] && <img src={l.photos[hero]} alt={l.model} className="w-full h-full object-cover" />}
            <div className="absolute top-4 left-4 flex gap-2">
              <Badge className="bg-amber-500/90 text-slate-950 font-mono text-xs uppercase tracking-wider">For sale</Badge>
              {reserved && <Badge className="bg-slate-950/80 text-white border border-slate-700 font-mono text-xs uppercase"><Lock className="w-3 h-3 mr-1" />Reserved</Badge>}
              {l.status === "sold" && <Badge className="bg-emerald-500/90 text-slate-950 font-mono text-xs uppercase">Sold</Badge>}
            </div>
            {!isSeller && <div className="absolute top-4 right-4"><HeartButton listingId={l.listing_id} hearts={l.hearts} size="lg" /></div>}
          </div>
          {l.photos?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto" data-testid="sale-gallery">
              {l.photos.map((p, i) => <button key={p} onClick={() => setHero(i)} className={`w-24 h-16 shrink-0 rounded-lg overflow-hidden border ${i === hero ? "border-amber-400" : "border-slate-800"}`}><img src={p} alt="" className="w-full h-full object-cover" /></button>)}
            </div>
          )}
          <div className="rise rise-1">
            <p className="font-mono text-xs tracking-widest uppercase text-amber-400 mb-2">{l.condition.replace("_", " ")} condition · {l.title_status} title</p>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">{l.year} {l.make} {l.model}</h1>
            <p className="text-slate-400 mt-1">{l.trim}</p>
            <div className="flex flex-wrap items-center gap-3 mt-3" data-testid="sale-vin-row">
              <VinBadge report={l.vin_report} />
              {l.vin && <span className="font-mono text-xs text-slate-400 tracking-widest">{l.vin}</span>}
              {l.vin_report?.decoded?.make && <span className="text-xs text-slate-500">NHTSA: {l.vin_report.decoded.year} {l.vin_report.decoded.make} {l.vin_report.decoded.model}{l.vin_report.decoded.body && ` · ${l.vin_report.decoded.body}`}{l.vin_report.decoded.plant_country && ` · built in ${l.vin_report.decoded.plant_country}`}</span>}
            </div>
            <div className="flex flex-wrap items-center gap-5 mt-4 text-sm text-slate-300">
              <span className="flex items-center gap-1.5"><Gauge className="w-4 h-4 text-amber-400" />{Number(l.mileage).toLocaleString()} mi</span>
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-amber-400" />{l.city}</span>
              <span className="flex items-center gap-1.5 text-slate-500"><Eye className="w-4 h-4" />{l.views} views</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 rise rise-2">
            {[{ icon: FileCheck, label: "Title", val: l.title_status }, { icon: Wrench, label: "Condition", val: l.condition.replace("_", " ") }, { icon: ShieldCheck, label: "Seller", val: l.seller_name }].map((s) => (
              <div key={s.label} className="p-4 rounded-xl bg-[#131722] border border-slate-800"><s.icon className="w-4 h-4 text-amber-400 mb-2" /><p className="text-xs font-mono uppercase tracking-widest text-slate-500">{s.label}</p><p className="font-display font-bold text-base capitalize truncate">{s.val}</p></div>
            ))}
          </div>
          <div className="rise rise-3"><h2 className="font-display text-2xl font-bold mb-3">About this car</h2><p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{l.description}</p></div>
          {l.features?.length > 0 && <div className="flex flex-wrap gap-2">{l.features.map((f) => <Badge key={f} className="bg-slate-800 text-slate-200 border-slate-700">{f}</Badge>)}</div>}
          <ShareButtons listing={l} />

          {(isSeller || user?.is_admin) && (
            <div className="rise rise-3" data-testid="seller-inbox">
              <h2 className="font-display text-2xl font-bold mb-3 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-amber-400" />Buyer inbox <span className="text-slate-500 text-base font-normal">({threads.length})</span></h2>
              {threads.length === 0 ? <p className="text-sm text-slate-500">No buyer messages yet.</p> : (
                <div className="space-y-4">
                  {threads.map((t) => (
                    <div key={t.thread_id} className="p-4 rounded-2xl bg-[#131722] border border-slate-800" data-testid={`inbox-thread-${t.thread_id}`}>
                      <p className="text-sm font-semibold mb-2">{t.buyer_name} <span className="text-slate-500 font-normal">· {t.messages.length} messages</span></p>
                      <SaleThread thread={t} meId={user.id} compact onUpdated={(d) => setThreads((xs) => xs.map((x) => x.thread_id === d.thread_id ? d : x))} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-28 h-fit rise rise-2">
          <div className="glass rounded-3xl p-6 space-y-4">
            <div className="flex items-baseline justify-between">
              <span className="font-display text-4xl font-extrabold" data-testid="sale-price">${Number(l.asking_price).toLocaleString()}</span>
              <span className="text-sm text-slate-400 font-mono uppercase tracking-widest">asking</span>
            </div>
            <FinanceHint amount={l.asking_price} context="sale" testId="finance-sale" />
            {isSeller ? (
              <div className="space-y-2" data-testid="seller-controls">
                <p className="text-xs text-slate-400">This is your listing.</p>
                {l.status === "reserved" && (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 space-y-2" data-testid="complete-sale-box">
                    <p className="text-xs text-emerald-300">Reserved with a ${Number(l.deposit_amount).toLocaleString()} deposit{expiresIn !== null && ` · ${expiresIn}h left`}</p>
                    <div className="flex gap-2">
                      <Input type="number" min={1} value={finalPrice} onChange={(e) => setFinalPrice(e.target.value)} placeholder={`Final price (${Number(l.asking_price).toLocaleString()})`} data-testid="final-price-input" className="bg-slate-900 border-slate-800 h-10" />
                    </div>
                    <Button onClick={completeSale} disabled={busy} data-testid="complete-sale-btn" className="w-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold"><CheckCircle2 className="w-4 h-4 mr-2" />Mark sold — apply deposit</Button>
                    <p className="text-[11px] text-slate-400">Deposit counts toward the price. Buyer gets a receipt with the balance due at handover.</p>
                  </div>
                )}
                {l.status === "sold" && l.deposit_applied != null && (
                  <div className="p-3 rounded-lg bg-slate-900 border border-slate-800 text-xs space-y-1" data-testid="sold-receipt">
                    <div className="flex justify-between"><span className="text-slate-400">Final price</span><span>${Number(l.final_price).toLocaleString()}</span></div>
                    <div className="flex justify-between text-emerald-300"><span>Deposit applied</span><span>−${Number(l.deposit_applied).toLocaleString()}</span></div>
                    <div className="flex justify-between font-semibold border-t border-slate-800 pt-1"><span>Balance due at handover</span><span>${Number(l.balance_due).toLocaleString()}</span></div>
                  </div>
                )}
                {l.status !== "sold" && l.status !== "reserved" && <Button onClick={() => setStatus("sold")} data-testid="mark-sold-btn" className="w-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold"><CheckCircle2 className="w-4 h-4 mr-2" />Mark as sold</Button>}
                {l.status === "active" && <Button variant="outline" onClick={() => setStatus("withdrawn")} data-testid="withdraw-btn" className="w-full bg-transparent border-slate-700">Withdraw listing</Button>}
                {l.status !== "active" && <Button variant="outline" onClick={() => setStatus("active")} data-testid="relist-btn" className="w-full bg-transparent border-slate-700">Re-activate</Button>}
              </div>
            ) : (
              <>
                {thread ? (
                  <div data-testid="buyer-thread"><p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Chat with {l.seller_name}</p><SaleThread thread={thread} meId={user?.id} onUpdated={setThread} /></div>
                ) : (
                  <Button onClick={openThread} variant="outline" data-testid="message-seller-btn" className="w-full h-12 bg-transparent border-slate-700 hover:bg-slate-800"><MessageSquare className="w-4 h-4 mr-2" />Message seller</Button>
                )}
                <MakeOffer listing={l} user={user} onSold={() => window.location.reload()} />
                {mine ? (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-300" data-testid="reserved-by-you">You've reserved this car{expiresIn !== null && ` — hold ends in ${expiresIn}h`}. The seller will reach out to complete the sale.</div>
                ) : l.status === "sold" && user && l.sold_to === user.id ? (
                  <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm space-y-1" data-testid="buyer-receipt">
                    <p className="text-emerald-300 font-semibold">It's yours! Receipt</p>
                    <div className="flex justify-between text-xs"><span className="text-slate-400">Final price</span><span>${Number(l.final_price).toLocaleString()}</span></div>
                    <div className="flex justify-between text-xs text-emerald-300"><span>Deposit applied</span><span>−${Number(l.deposit_applied).toLocaleString()}</span></div>
                    <div className="flex justify-between text-xs font-semibold border-t border-emerald-500/30 pt-1"><span>Balance due at handover</span><span>${Number(l.balance_due).toLocaleString()}</span></div>
                  </div>
                ) : (
                  <Button onClick={reserve} disabled={busy || l.status !== "active"} data-testid="reserve-btn" className="w-full h-12 bg-amber-500 text-slate-950 hover:bg-amber-400 font-semibold btn-glow">
                    <Lock className="w-4 h-4 mr-2" />{reserved ? "Already reserved" : l.status === "sold" ? "Sold" : `Reserve — $${Number(l.deposit_amount).toLocaleString()} deposit`}
                  </Button>
                )}
                <p className="text-xs text-slate-500 text-center">Refundable deposit holds the car for 72 hours. Secured by Stripe · Card, Klarna or Affirm.</p>
              </>
            )}
            <Link to="/buy" className="block text-center text-xs text-slate-500 hover:text-slate-300">← Back to cars for sale</Link>
          </div>
        </aside>
      </div>
    </main>
  );
}
